// store/location.js - the patient's position for search and distances (TB-063).
// Replaces the old undefined user-coords global. `coords` is always a valid
// numeric pair: TB-161 origin priority is the patient's saved address, then the
// GPS fix, then the Bahrain centre.
import { defineStore } from 'pinia'
import { BAHRAIN_CENTER, MAPS_REGION, inBahrain, isValidCoord, mapsLanguage } from '../config/map'
import { partyFor, storedRole } from '../utils/sessionExpiry'

const LEGACY_LAT = 'latitude'
const LEGACY_LNG = 'longitude'
const STORAGE_KEY = 'tb-location'
// A GPS fix is personal data: keep it coarse (4 decimals, about 11 m) and
// forget it after a day.
const FIX_DECIMALS = 4
const GPS_MAX_AGE_MS = 24 * 60 * 60 * 1000

function round (value) {
  const f = Math.pow(10, FIX_DECIMALS)
  return Math.round(Number(value) * f) / f
}

function expired (state) {
  return state.source === 'gps' && (!state.updatedAt || Date.now() - state.updatedAt > GPS_MAX_AGE_MS)
}

function usable (state) {
  return isValidCoord(state.lat, state.lng) && !expired(state)
}

export const useLocationStore = defineStore('location', {
  state: () => ({
    lat: null,
    lng: null,
    source: 'default', // gps | default | manual
    homeLat: null, // TB-161: the patient's saved address pin
    homeLng: null,
    homeFor: null, // token tail the pin was read for
    permission: 'unknown', // unknown | granted | denied | unsupported
    updatedAt: null
  }),
  getters: {
    hasFix: (state) => usable(state),
    hasHome: (state) => isValidCoord(state.homeLat, state.homeLng),
    // True when distances are measured from the Bahrain centre, not the patient.
    isDefault: (state) => !usable(state) && !isValidCoord(state.homeLat, state.homeLng),
    originSource: (state) => (isValidCoord(state.homeLat, state.homeLng) ? 'home' : usable(state) ? 'gps' : 'default'),
    coords (state) {
      if (isValidCoord(state.homeLat, state.homeLng)) {
        return { lat: Number(state.homeLat), lng: Number(state.homeLng) }
      }
      if (usable(state)) {
        return { lat: Number(state.lat), lng: Number(state.lng) }
      }
      return { lat: BAHRAIN_CENTER.lat, lng: BAHRAIN_CENTER.lng }
    }
  },
  actions: {
    // Called once per page that needs coords. Moves the old localStorage
    // latitude/longitude keys into the store and reads the permission state
    // without prompting.
    init () {
      // A persisted fix older than a day falls back to the default.
      if (isValidCoord(this.lat, this.lng) && expired(this.$state)) this.clearFix()
      if (!this.hasFix) {
        try {
          const lat = localStorage.getItem(LEGACY_LAT)
          const lng = localStorage.getItem(LEGACY_LNG)
          if (isValidCoord(lat, lng)) this.setFix(Number(lat), Number(lng), 'gps')
        } catch (e) { /* storage blocked */ }
      }
      try {
        localStorage.removeItem(LEGACY_LAT)
        localStorage.removeItem(LEGACY_LNG)
      } catch (e) { /* storage blocked */ }

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        this.permission = 'unsupported'
        return Promise.resolve(this.permission)
      }
      if (!navigator.permissions || !navigator.permissions.query) {
        return Promise.resolve(this.permission)
      }
      return navigator.permissions.query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'granted') this.permission = 'granted'
          else if (status.state === 'denied') {
            // Revoked since the fix was saved: stop sending it.
            this.permission = 'denied'
            if (this.source === 'gps') this.clearFix()
          } else this.permission = 'unknown'
          return this.permission
        })
        .catch(() => this.permission)
    },
    // Ask the browser for a fix. Always resolves with `coords` (the fix, or
    // the Bahrain centre when denied, unsupported or timed out).
    request () {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        this.permission = 'unsupported'
        return Promise.resolve(this.coords)
      }
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.permission = 'granted'
            this.setFix(position.coords.latitude, position.coords.longitude, 'gps')
            resolve(this.coords)
          },
          (error) => {
            if (error && error.code === 1) {
              // PERMISSION_DENIED: forget an old fix so we do not keep using it.
              this.permission = 'denied'
              this.clearFix()
            }
            resolve(this.coords)
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        )
      })
    },
    setHome (lat, lng) {
      if (isValidCoord(lat, lng)) {
        this.homeLat = Number(lat)
        this.homeLng = Number(lng)
        this.homeFor = tokenTail()
      }
    },
    clearHome () {
      this.homeLat = null
      this.homeLng = null
      this.homeFor = null
    },
    // TB-161: read the signed-in patient's saved address pin once per session.
    // A patient with an address but no pin is geocoded in the browser with the
    // Maps key the page already loads, then saved (unverified) via update-profile.
    // Always resolves with `coords`; never throws.
    loadHome () {
      const tail = tokenTail()
      if (!tail || !isPatient()) {
        if (this.homeLat !== null) this.clearHome()
        return Promise.resolve(this.coords)
      }
      if (this.homeFor && this.homeFor !== tail) this.clearHome()
      if (homeLoad && homeLoad.tail === tail) return homeLoad.promise
      // The first geocode can take a moment: do not hold a page for more than 3 s.
      const read = readHome(this).catch(() => {})
      const promise = Promise.race([read, new Promise((resolve) => setTimeout(resolve, 3000))]).then(() => this.coords)
      homeLoad = { tail, promise }
      return promise
    },
    setManual (lat, lng) {
      if (isValidCoord(lat, lng)) this.setFix(Number(lat), Number(lng), 'manual')
    },
    setFix (lat, lng, source) {
      this.lat = round(lat)
      this.lng = round(lng)
      this.source = source
      this.updatedAt = Date.now()
    },
    clearFix () {
      this.lat = null
      this.lng = null
      this.source = 'default'
      this.updatedAt = null
    },
    // Logout and session expiry: forget everything, in memory and on disk.
    reset () {
      homeLoad = null
      this.$reset()
      try { localStorage.removeItem(STORAGE_KEY) } catch (e) { /* storage blocked */ }
    }
  },
  persist: {
    key: STORAGE_KEY,
    storage: localStorage
  }
})

function tokenTail () {
  try { return (localStorage.getItem('accessToken') || '').slice(-16) } catch (e) { return '' }
}

function isPatient () {
  try { return partyFor(storedRole(localStorage), '') === 'patient' && !!storedRole(localStorage) } catch (e) { return false }
}

let homeLoad = null
const GEOCODE_RETRY_MS = 24 * 60 * 60 * 1000

async function readHome (store) {
  const http = (await import('../axios.js')).default
  const base = process.env.VUE_APP_API_BASEURL_USER
  if (!base) return
  const res = await http.get(`${base}get-profile`)
  const p = (res && res.data && res.data.data) || null
  if (!p) return
  if (isValidCoord(p.latitude, p.longitude)) {
    store.setHome(p.latitude, p.longitude)
    return
  }
  store.clearHome()
  const text = [p.address, p.city, p.country].map((v) => String(v || '').trim()).filter(Boolean).join(', ')
  if (!String(p.address || '').trim() || !text) return
  const key = `tb-geocoded:${p.userId || p.id || ''}`
  try {
    const last = Number(localStorage.getItem(key))
    if (last && Date.now() - last < GEOCODE_RETRY_MS) return
    localStorage.setItem(key, String(Date.now()))
  } catch (e) { /* storage blocked: try once per page load */ }
  const hit = await geocodeAddress(text, p.country)
  if (!hit) return
  await http.patch(`${base}update-profile`, {
    firstName: p.firstName,
    lastName: p.lastName,
    phoneNumber: p.phoneNumber,
    address: p.address,
    city: p.city || '',
    country: p.country || 'Bahrain',
    latitude: hit.lat,
    longitude: hit.lng,
    placeId: hit.placeId || null
  })
  store.setHome(hit.lat, hit.lng)
}

function mapsReady (timeoutMs) {
  return new Promise((resolve) => {
    const started = Date.now()
    const tick = () => {
      const g = typeof window !== 'undefined' && window.google && window.google.maps
      if (g && g.Geocoder) return resolve(g)
      if (Date.now() - started > timeoutMs) return resolve(null)
      setTimeout(tick, 250)
    }
    tick()
  })
}

async function geocodeAddress (text, country) {
  const g = await mapsReady(8000)
  if (!g) return null
  return new Promise((resolve) => {
    new g.Geocoder().geocode({ address: text, region: MAPS_REGION, language: mapsLanguage() }, (results, status) => {
      const best = status === 'OK' && Array.isArray(results) ? results[0] : null
      const loc = best && best.geometry && best.geometry.location
      if (!loc) return resolve(null)
      const lat = loc.lat()
      const lng = loc.lng()
      const bahrain = !country || /bahrain/i.test(String(country))
      if (!isValidCoord(lat, lng) || (bahrain && !inBahrain(lat, lng))) return resolve(null)
      resolve({ lat, lng, placeId: best.place_id })
    })
  })
}

// A profile save may have moved the home pin: read it again on the next page.
export function refreshHomeLocation () {
  homeLoad = null
}

// For logout paths outside components (axios 401). Safe before Pinia is ready.
export function resetLocationStore () {
  try {
    useLocationStore().reset()
  } catch (e) {
    try { localStorage.removeItem(STORAGE_KEY) } catch (err) { /* storage blocked */ }
  }
}
